import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogContentUltraPriority,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContentUltraPriority, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useRecuperacaoItem } from '@/hooks/useRecuperacaoItem';
import { CheckCircle2, Clock } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

interface RecuperarItemModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  cancelamentoId: string;
  itemId: string;
  itemNome?: string;
  motivoTitulo?: string;
  dataLimite?: string;
}

export function RecuperarItemModal({
  open,
  onOpenChange,
  cancelamentoId,
  itemId,
  itemNome,
  motivoTitulo,
  dataLimite,
}: RecuperarItemModalProps) {
  const [observacao, setObservacao] = useState('');
  // (Douglas 30/07) "preciso que quando um pedido é recuperado tenhamos os
  // motivos da recuperação". Antes só existia texto livre — impossível
  // agrupar ou comparar. Agora o motivo é catalogado, e é ele que aparece
  // na planilha mãe no lugar do motivo do cancelamento.
  const [motivoRecuperacaoId, setMotivoRecuperacaoId] = useState('');

  const { data: motivosRecuperacao = [] } = useQuery({
    queryKey: ['motivos-recuperacao-ativos'],
    enabled: open,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('motivos_recuperacao')
        .select('id, titulo, descricao')
        .eq('ativo', true)
        .order('ordem');
      if (error) throw error;
      return data || [];
    },
  });

  const { recuperarItem, isRecuperando } = useRecuperacaoItem();

  const handleRecuperar = async () => {
    await recuperarItem({
      cancelamentoId,
      itemId,
      observacao: observacao || undefined,
      motivoRecuperacaoId: motivoRecuperacaoId || undefined,
    });

    onOpenChange(false);
    setObservacao('');
    setMotivoRecuperacaoId('');
  };

  const prazoRestante = dataLimite
    ? formatDistanceToNow(new Date(dataLimite), { locale: ptBR, addSuffix: true })
    : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContentUltraPriority className="sm:max-w-[500px]" onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-success" />
            Recuperar Item Cancelado
          </DialogTitle>
          <DialogDescription>
            {itemNome && <span className="font-medium">{itemNome}</span>}
            {!itemNome && 'O item será restaurado ao status anterior ao cancelamento.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {motivoTitulo && (
            <div className="rounded-lg border bg-muted/50 p-3 text-sm">
              <p className="text-muted-foreground">
                <span className="font-medium">Motivo do cancelamento:</span> {motivoTitulo}
              </p>
            </div>
          )}

          {prazoRestante && (
            <div className="rounded-lg border border-primary/20 bg-primary/5 p-3 text-sm">
              <p className="flex items-center gap-2 text-primary">
                <Clock className="h-4 w-4" />
                <span className="font-medium">Prazo para recuperação: {prazoRestante}</span>
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="motivo-recuperacao">
              Motivo da Recuperação <span className="text-destructive">*</span>
            </Label>
            <Select value={motivoRecuperacaoId} onValueChange={setMotivoRecuperacaoId}>
              <SelectTrigger id="motivo-recuperacao">
                <SelectValue placeholder="Por que este item voltou?" />
              </SelectTrigger>
              {/* UltraPriority (z-11100) porque este modal é DialogContentUltraPriority
                  (z-11000): com o SelectContent comum (z-10150) o dropdown abria ATRÁS
                  do modal e a equipe não conseguia escolher o motivo — o botão de
                  confirmar nunca liberava. (Douglas 03/08) */}
              <SelectContentUltraPriority>
                {motivosRecuperacao.map((m) => (
                  <SelectItem key={m.id} value={m.id}>{m.titulo}</SelectItem>
                ))}
              </SelectContentUltraPriority>
            </Select>
            <p className="text-xs text-muted-foreground">
              Este motivo substitui o motivo do cancelamento na planilha assim que o item volta.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="observacao">
              Justificativa da Recuperação <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="observacao"
              placeholder="O que foi resolvido? Ex: Cliente entrou em contato informando que o problema foi solucionado pela operadora..."
              value={observacao}
              onChange={(e) => setObservacao(e.target.value)}
              rows={4}
              maxLength={500}
              required
            />
            <p className={`text-xs ${observacao.trim().length < 20 ? 'text-destructive' : 'text-muted-foreground'}`}>
              {observacao.trim().length}/500 caracteres {observacao.trim().length < 20 && `(mínimo 20)`}
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isRecuperando}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleRecuperar}
            disabled={isRecuperando || observacao.trim().length < 20 || !motivoRecuperacaoId}
          >
            {isRecuperando ? 'Recuperando...' : 'Confirmar Recuperação'}
          </Button>
        </DialogFooter>
      </DialogContentUltraPriority>
    </Dialog>
  );
}
